/**
 * 安装引导页的判定与文案（任务 16b，纯逻辑、可单测）。
 *
 * 口径：
 * - 运行环境只看 **UA 串 + isStandalone** 两个输入，组件不做任何嗅探，
 *   方便单测把真实 UA 串钉死（三端 e2e 的 UA 天然落在不同分支）。
 * - 描述文件只对 iOS 有意义；安卓给「添加到主屏幕」说明，桌面让用户换手机打开。
 * - App 内置浏览器（微信/QQ/抖音…）**下载不了描述文件**（点了没反应），
 *   必须先提示「在 Safari 中打开」——这是 PROMPT 与 docs/ios-webclip.md §3 的硬要求。
 * - standalone（已装到主屏）下一律不出引导（PROMPT 明文要求）。
 *
 * 文案全部在本文件，组件只负责渲染，改文案不碰组件。
 */
import { BRAND_NAME } from '~/brand';
import { hashUrlFor } from '~/nav/hashLocation';

/**
 * 描述文件的托管路径。与 `deploy/README.md` §4 的约定（同名文件 +
 * `Content-Type: application/x-apple-aspen-config`）是同一处约定，改这里必须同步改部署配置。
 * 它不是 Vite 构建产物，由部署流水线单独上传；SW 不接管该路径（docs/sw.md 策略表）。
 */
export const MOBILECONFIG_PATH = '/linghu-chat.mobileconfig';

/** 安装引导页自身的路径（routes.ts 的 `install` 分支同值；复制链接给的就是它） */
export const INSTALL_PATH = '/install';

export type InstallOs = 'ios' | 'android' | 'other';
/** safari = iOS 上能装描述文件的唯一浏览器；in-app = App 内置 WebView；other = 其余 */
export type InstallBrowser = 'safari' | 'in-app' | 'other';

export interface InstallHost {
  readonly os: InstallOs;
  readonly browser: InstallBrowser;
  /** browser === 'in-app' 时的 App 名（认不出具体 App 时为「当前 App」） */
  readonly appName?: string;
}

/**
 * App 内置浏览器识别表（命中即 in-app）。顺序有讲究：微信/QQ 的 WebView UA 里
 * 同样带 `Safari/`，所以 in-app 必须在 Safari 判定**之前**命中。
 * 认不出牌子的 WebView 由兜底规则（iOS 上无 `Safari/` 标记）处理。
 */
const IN_APP_PATTERNS: readonly (readonly [RegExp, string])[] = [
  [/MicroMessenger/i, '微信'],
  [/\bQQ\/|QQTheme|MQQBrowser/i, 'QQ'],
  [/Weibo/i, '微博'],
  [/DingTalk/i, '钉钉'],
  [/AlipayClient/i, '支付宝'],
  [/aweme|BytedanceWebview/i, '抖音'],
  [/baiduboxapp/i, '百度'],
  [/FBAN|FBAV|Instagram/i, 'Facebook'],
  [/\bLine\//i, 'LINE'],
];

/** iOS 上套壳内核的第三方浏览器：装不了描述文件，与 in-app 一样要引到 Safari */
const IOS_THIRD_PARTY = /CriOS|FxiOS|EdgiOS|OPiOS|UCBrowser|Quark/i;

/**
 * UA → 运行环境。
 *
 * 已知局限（刻意不处理）：iPadOS 13+ 的 Safari UA 自称 `Macintosh`，会落到
 * os='other'（桌面分支）。桌面分支的文案写的是「用 iPhone / iPad 的 Safari 打开本页」，
 * iPad 用户看到也说得通；要精确区分得靠 `maxTouchPoints` 嗅探，不值得。
 */
export function detectInstallHost(userAgent: string): InstallHost {
  const ua = userAgent;
  const os: InstallOs = /iPhone|iPad|iPod/i.test(ua)
    ? 'ios'
    : /Android/i.test(ua)
      ? 'android'
      : 'other';

  for (const [pattern, appName] of IN_APP_PATTERNS) {
    if (pattern.test(ua)) return { os, browser: 'in-app', appName };
  }
  if (os === 'ios') {
    if (IOS_THIRD_PARTY.test(ua)) return { os, browser: 'other' };
    // 认不出牌子的 iOS WebView（UA 不带 Safari/ 标记）：当成内置浏览器提示换 Safari
    if (!/Safari\//i.test(ua)) return { os, browser: 'in-app', appName: '当前 App' };
    return { os, browser: 'safari' };
  }
  return { os, browser: 'other' };
}

export type InstallVariant =
  /** 已装到主屏、正在全屏运行：不出引导 */
  | 'installed'
  /** App 内置浏览器：先引到 Safari / 系统浏览器 */
  | 'in-app'
  /** iOS Safari：正主，给下载按钮 + 安装步骤 */
  | 'ios-safari'
  /** iOS 第三方浏览器（Chrome/Edge/夸克…）：装不了，引到 Safari */
  | 'ios-other-browser'
  /** 安卓：描述文件不适用，给「添加到主屏幕」 */
  | 'android'
  /** 桌面：网页版直接用；要装主屏请换手机 */
  | 'desktop';

export interface InstallGuideView {
  readonly variant: InstallVariant;
  readonly title: string;
  readonly intro: string;
  /** 有序步骤（可为空） */
  readonly steps: readonly string[];
  /** 是否显示「安装到桌面」下载按钮（只有 iOS Safari 点了有用） */
  readonly showDownload: boolean;
  /** 是否显示「复制本页链接」按钮 */
  readonly showCopyLink: boolean;
  /** 补充说明（有效期、找不到入口、未验证提示、卸载方式…） */
  readonly footnotes: readonly string[];
}

const IOS_SAFARI_STEPS = [
  '点下方「安装到桌面」，Safari 询问时选「允许」',
  '打开 iPhone 的「设置」，顶部会出现「已下载描述文件」，点进去',
  '右上角「安装」→ 输入锁屏密码 → 再次点「安装」',
  `回到主屏幕，点新出现的「${BRAND_NAME}」图标，即可全屏使用`,
] as const;

const IOS_SAFARI_FOOTNOTES = [
  '描述文件下载后约 8 分钟内未安装就会失效，超时请回到本页重新下载。',
  '「设置」顶部没有「已下载描述文件」时：设置 →「通用」→「VPN 与设备管理」里选它。',
  '安装界面顶部显示「未验证」说明描述文件未经证书签名，确认是从本站下载的再继续。',
  '想卸载：长按主屏图标删除，或在「VPN 与设备管理」中移除该描述文件。',
] as const;

const TO_SAFARI_STEPS = [
  '点「复制本页链接」',
  '回到桌面打开 Safari 浏览器',
  '把链接粘贴到地址栏并打开，按页面提示安装',
] as const;

/** (运行环境, 是否 standalone) → 这一屏长什么样。standalone 优先级最高。 */
export function installGuideView(host: InstallHost, isStandalone: boolean): InstallGuideView {
  if (isStandalone) {
    return {
      variant: 'installed',
      title: '已安装到主屏幕',
      intro: '当前已经在全屏应用模式下运行，无需再次安装。',
      steps: [],
      showDownload: false,
      showCopyLink: false,
      footnotes: ['想卸载：长按主屏图标删除，或在「设置 → 通用 → VPN 与设备管理」中移除描述文件。'],
    };
  }

  if (host.browser === 'in-app') {
    const appName = host.appName ?? '当前 App';
    const toSafari = host.os !== 'android';
    return {
      variant: 'in-app',
      title: toSafari ? '请在 Safari 中打开' : '请在系统浏览器中打开',
      intro: `${appName}的内置浏览器无法下载安装用的描述文件，需要先在${
        toSafari ? ' Safari ' : '系统浏览器'
      }中打开本页。`,
      steps: [
        `点${appName}右上角的「…」，选「在${toSafari ? ' Safari ' : '浏览器'}中打开」`,
        '没有这个菜单项时，用下面的按钮复制链接，再手动粘贴到浏览器地址栏',
      ],
      showDownload: false,
      showCopyLink: true,
      footnotes: [],
    };
  }

  switch (host.os) {
    case 'ios':
      return host.browser === 'safari'
        ? {
            variant: 'ios-safari',
            title: '安装到主屏幕',
            intro: '安装后得到一个桌面图标，点开即全屏运行，和 App 一样。',
            steps: IOS_SAFARI_STEPS,
            showDownload: true,
            showCopyLink: false,
            footnotes: IOS_SAFARI_FOOTNOTES,
          }
        : {
            variant: 'ios-other-browser',
            title: '请在 Safari 中打开',
            intro: 'iPhone 上只有 Safari 能安装描述文件，其他浏览器点了不会有反应。',
            steps: TO_SAFARI_STEPS,
            showDownload: false,
            showCopyLink: true,
            footnotes: [],
          };
    case 'android':
      return {
        variant: 'android',
        title: '添加到主屏幕',
        intro: '安卓不需要描述文件：用浏览器自带的「添加到主屏幕」即可获得全屏图标。',
        steps: [
          '点浏览器右上角菜单',
          '选「安装应用」或「添加到主屏幕」',
          '确认后回到桌面，点新图标即可全屏使用',
        ],
        showDownload: false,
        showCopyLink: false,
        footnotes: ['描述文件（.mobileconfig）只适用于 iPhone / iPad。'],
      };
    case 'other':
      return {
        variant: 'desktop',
        title: '在手机上安装',
        intro: '桌面浏览器直接用网页版即可。想把它装到 iPhone 主屏，请用 iPhone / iPad 的 Safari 打开本页。',
        steps: TO_SAFARI_STEPS,
        showDownload: false,
        showCopyLink: true,
        footnotes: [],
      };
  }
}

/**
 * 复制给用户的链接：当前站点的安装页绝对地址。
 * 21ah-5 起是 **hash 形态**（`https://host/#/install`）——路由换了 hash 模式后，
 * `/install` 这种裸路径要靠服务端重写才活得下来，而 hash 模式的前提正是「不依赖
 * 那条重写规则」。粘到 Safari 地址栏的链接必须是真能打开的那一个。
 */
export function installPageUrl(origin: string, base = '/'): string {
  return `${origin.replace(/\/+$/, '')}${hashUrlFor(base, INSTALL_PATH)}`;
}
